import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Container, IconButton, Button, Divider,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
    ArrowBackOutlined, ContentCopyOutlined, CheckOutlined,
    CardGiftcardOutlined, PeopleOutlined, LockOutlined,
    ShareOutlined, WhatsApp, CheckCircleOutlined,
} from '@mui/icons-material';

const B = {
    deep:    '#0D0820',
    dark:    '#1A0F3D',
    mid:     '#2D1B69',
    purple:  '#7B5EA7',
    light:   '#9B7EC8',
    accent:  '#C8B8E8',
    lavender:'#F7F4FC',
    ink:     '#0D0A1A',
    green:   '#00C896',
    greenDim:'#00C89618',
    gold:    '#F5C842',
    amber:   '#FF9500',
};

const REFERRAL_CODE = 'NGWALLET2024';

const rewards = [
    { emoji: '🎁', label: 'Your friend signs up',      reward: 'They get ₦500 bonus'       },
    { emoji: '💸', label: 'They make first transaction', reward: 'You earn ₦1,000 cashback'  },
    { emoji: '🏆', label: 'Refer 5 friends',            reward: 'Unlock Elite status'        },
];

const stats = [
    { label: 'Total Referred', value: '3',      color: B.purple },
    { label: 'Pending',        value: '1',      color: B.amber  },
    { label: 'Earned',         value: '₦2,000', color: B.green  },
];

const ReferAndEarnPage: React.FC = () => {
    const navigate = useNavigate();
    const theme    = useTheme();
    const isDark   = theme.palette.mode === 'dark';

    const [copied,    setCopied]    = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(REFERRAL_CODE);
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
    };

    const handleCopyLink = () => {
        navigator.clipboard.writeText(`https://ngcredit.app/signup?ref=${REFERRAL_CODE}`);
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2500);
    };

    const handleWhatsApp = () => {
        const msg = encodeURIComponent(`Join NG Credit and get ₦500 bonus! Use my code: ${REFERRAL_CODE}\nhttps://ngcredit.app/signup?ref=${REFERRAL_CODE}`);
        window.open(`https://wa.me/?text=${msg}`, '_blank');
    };

    const surfaceBg  = isDark ? alpha(B.mid, .55) : '#fff';
    const surfaceBdr = `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}`;
    const surfaceShd = `0 4px 28px ${alpha(B.dark, isDark ? .25 : .06)}`;

    return (
        <Box sx={{ minHeight: '100vh', background: isDark ? B.deep : B.lavender, pb: 10, fontFamily: "'DM Sans', system-ui, sans-serif" }}>

            {/* ══ HERO ═══════════════════════════════════════════════ */}
            <Box sx={{
                background: `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${B.purple} 100%)`,
                mt: { xs: '-56px', sm: 0 },
                pt: { xs: 'calc(56px + 44px)', sm: '44px' },
                pb: 14, px: 3, position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -70, right: -50, width: 260, height: 260, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.gold, .16)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', bottom: -20, left: -40, width: 180, height: 180, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.light, .1)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

                <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <IconButton onClick={() => navigate(-1)} sx={{ color: alpha('#fff', .7), background: alpha('#fff', .1), border: `1px solid ${alpha('#fff', .15)}`, width: 36, height: 36 }}>
                            <ArrowBackOutlined sx={{ fontSize: 17 }} />
                        </IconButton>
                        <Box>
                            <Typography sx={{ color: alpha('#fff', .35), fontSize: '.62rem', letterSpacing: '.18em', textTransform: 'uppercase' }}>NG Credit</Typography>
                            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.2rem', letterSpacing: '-.03em', fontFamily: "'Instrument Serif', serif" }}>Refer & Earn</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ width: 36, height: 36, borderRadius: 2.5, background: alpha(B.gold, .15), border: `1px solid ${alpha(B.gold, .3)}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CardGiftcardOutlined sx={{ color: B.gold, fontSize: 18 }} />
                    </Box>
                </Box>

                {/* Stats row */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
                    {stats.map((s, i) => (
                        <Box key={i} sx={{ p: 1.5, borderRadius: 2.5, background: alpha('#fff', .08), border: `1px solid ${alpha('#fff', .1)}`, textAlign: 'center' }}>
                            <Typography sx={{ color: s.color, fontFamily: "'Instrument Serif', serif", fontSize: '1.4rem', fontWeight: 400, lineHeight: 1 }}>{s.value}</Typography>
                            <Typography sx={{ color: alpha('#fff', .38), fontSize: '.6rem', letterSpacing: '.06em', mt: .3, textTransform: 'uppercase' }}>{s.label}</Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            <Container maxWidth="sm" sx={{ mt: -8, position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>

                {/* ── Referral code card ── */}
                <Box sx={{ mb: 2, borderRadius: 3, overflow: 'hidden', background: `linear-gradient(145deg, ${B.dark} 0%, ${B.mid} 100%)`, boxShadow: `0 16px 48px ${alpha(B.deep, .45)}` }}>
                    <Box sx={{ p: 2.5 }}>
                        <Typography sx={{ color: alpha('#fff', .35), fontSize: '.6rem', letterSpacing: '.18em', textTransform: 'uppercase', mb: 2, textAlign: 'center' }}>Your Referral Code</Typography>

                        {/* Code display */}
                        <Box sx={{
                            py: 2, px: 2.5, mb: 2, borderRadius: 2.5,
                            background: alpha('#fff', .06),
                            border: `1.5px dashed ${alpha(B.gold, .4)}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        }}>
                            <Typography sx={{ color: B.gold, fontFamily: "'Instrument Serif', serif", fontSize: '1.8rem', fontWeight: 400, letterSpacing: '.12em' }}>
                                {REFERRAL_CODE}
                            </Typography>
                            <IconButton onClick={handleCopy} sx={{
                                color: copied ? B.green : alpha('#fff', .5),
                                background: copied ? alpha(B.green, .12) : alpha('#fff', .08),
                                border: `1px solid ${copied ? alpha(B.green, .3) : alpha('#fff', .12)}`,
                                borderRadius: 2, p: 1, transition: 'all .2s',
                            }}>
                                {copied ? <CheckOutlined sx={{ fontSize: 18 }} /> : <ContentCopyOutlined sx={{ fontSize: 18 }} />}
                            </IconButton>
                        </Box>

                        {copied && (
                            <Box display="flex" alignItems="center" justifyContent="center" gap={.75} mb={1.5}>
                                <CheckCircleOutlined sx={{ fontSize: 14, color: B.green }} />
                                <Typography sx={{ color: B.green, fontSize: '.72rem', fontWeight: 700 }}>Code copied to clipboard</Typography>
                            </Box>
                        )}

                        <Typography sx={{ color: alpha('#fff', .35), fontSize: '.74rem', textAlign: 'center', lineHeight: 1.6, mb: 2.5 }}>
                            Share with friends — they get <span style={{ color: B.gold, fontWeight: 700 }}>₦500 bonus</span> on sign-up,<br />you earn <span style={{ color: B.green, fontWeight: 700 }}>₦1,000 cashback</span> on their first transaction.
                        </Typography>

                        <Divider sx={{ borderColor: alpha('#fff', .07), mb: 2.5 }} />

                        {/* Share buttons */}
                        <Box display="flex" gap={1.25}>
                            <Button fullWidth onClick={handleWhatsApp} startIcon={<WhatsApp sx={{ fontSize: 18 }} />}
                                    sx={{ background: alpha('#25D366', .15), color: '#25D366', borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontSize: '.8rem', py: 1.2, border: `1px solid ${alpha('#25D366', .25)}`, '&:hover': { background: alpha('#25D366', .25) } }}>
                                WhatsApp
                            </Button>
                            <Button fullWidth onClick={handleCopyLink} startIcon={linkCopied ? <CheckOutlined sx={{ fontSize: 18 }} /> : <ShareOutlined sx={{ fontSize: 18 }} />}
                                    sx={{ background: alpha('#fff', .08), color: linkCopied ? B.green : '#fff', borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontSize: '.8rem', py: 1.2, border: `1px solid ${alpha('#fff', .12)}`, '&:hover': { background: alpha('#fff', .14) } }}>
                                {linkCopied ? 'Copied!' : 'Copy Link'}
                            </Button>
                        </Box>
                    </Box>
                </Box>

                {/* ── How it works ── */}
                <Box sx={{ mb: 2, borderRadius: 3, background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd, overflow: 'hidden' }}>
                    <Box sx={{ px: 2.5, pt: 2.25, pb: 2, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}` }}>
                        <Box sx={{ width: 32, height: 32, borderRadius: 2, background: alpha(B.purple, .1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <PeopleOutlined sx={{ color: B.purple, fontSize: 16 }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontWeight: 700, fontSize: '.88rem', letterSpacing: '-.02em', color: isDark ? '#fff' : B.ink }}>How it works</Typography>
                            <Typography sx={{ fontSize: '.68rem', color: alpha(isDark ? '#fff' : B.ink, .35) }}>3 simple steps</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 0 }}>
                        {rewards.map((r, i) => (
                            <Box key={i}>
                                <Box display="flex" alignItems="flex-start" gap={2} py={i === 0 ? 0 : 2}>
                                    {/* Step number + connector */}
                                    <Box display="flex" flexDirection="column" alignItems="center" sx={{ flexShrink: 0, width: 32 }}>
                                        <Box sx={{
                                            width: 32, height: 32, borderRadius: '50%',
                                            background: `linear-gradient(135deg, ${B.purple}, ${B.light})`,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            boxShadow: `0 4px 12px ${alpha(B.purple, .4)}`,
                                            fontSize: '1rem',
                                        }}>
                                            {r.emoji}
                                        </Box>
                                        {i < rewards.length - 1 && (
                                            <Box sx={{ width: 2, height: 32, background: alpha(isDark ? '#fff' : B.dark, .07), my: .5, borderRadius: 2 }} />
                                        )}
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontWeight: 600, fontSize: '.82rem', color: isDark ? '#fff' : B.ink, letterSpacing: '-.01em', mb: .3 }}>{r.label}</Typography>
                                        <Box sx={{ display: 'inline-flex', alignItems: 'center', background: alpha(B.green, .1), border: `1px solid ${alpha(B.green, .2)}`, borderRadius: 8, px: 1, py: .3 }}>
                                            <Typography sx={{ color: B.green, fontSize: '.68rem', fontWeight: 700 }}>{r.reward}</Typography>
                                        </Box>
                                    </Box>
                                </Box>
                                {i < rewards.length - 1 && <Divider sx={{ borderColor: alpha(isDark ? '#fff' : B.dark, .055) }} />}
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* ── Referred friends ── */}
                <Box sx={{ borderRadius: 3, background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd, overflow: 'hidden' }}>
                    <Box sx={{ px: 2.5, pt: 2.25, pb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}` }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '.88rem', letterSpacing: '-.02em', color: isDark ? '#fff' : B.ink }}>Your Referrals</Typography>
                        <Box sx={{ background: alpha(B.purple, .1), border: `1px solid ${alpha(B.purple, .2)}`, borderRadius: 8, px: 1, py: .3 }}>
                            <Typography sx={{ color: B.purple, fontSize: '.65rem', fontWeight: 700 }}>3 FRIENDS</Typography>
                        </Box>
                    </Box>
                    {[
                        { name: 'Emeka O.',  status: 'Completed', earned: '₦1,000', color: B.green  },
                        { name: 'Fatima Y.', status: 'Completed', earned: '₦1,000', color: B.green  },
                        { name: 'David A.',  status: 'Pending',   earned: '—',       color: B.amber  },
                    ].map((friend, i, arr) => (
                        <Box key={i}>
                            <Box sx={{ px: 2.5, py: 1.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box display="flex" alignItems="center" gap={1.5}>
                                    <Box sx={{ width: 36, height: 36, borderRadius: '50%', background: `linear-gradient(135deg, ${B.purple}, ${B.light})`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Typography sx={{ color: '#fff', fontSize: '.72rem', fontWeight: 700 }}>{friend.name.charAt(0)}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontWeight: 600, fontSize: '.82rem', color: isDark ? '#fff' : B.ink, letterSpacing: '-.01em' }}>{friend.name}</Typography>
                                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: .4, background: alpha(friend.color, .1), border: `1px solid ${alpha(friend.color, .2)}`, borderRadius: 6, px: .8, py: .15, mt: .25 }}>
                                            <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: friend.color }} />
                                            <Typography sx={{ color: friend.color, fontSize: '.6rem', fontWeight: 700, letterSpacing: '.04em' }}>{friend.status.toUpperCase()}</Typography>
                                        </Box>
                                    </Box>
                                </Box>
                                <Typography sx={{ fontWeight: 700, fontSize: '.88rem', color: friend.status === 'Completed' ? B.green : alpha(isDark ? '#fff' : B.ink, .3), fontVariantNumeric: 'tabular-nums' }}>{friend.earned}</Typography>
                            </Box>
                            {i < arr.length - 1 && <Divider sx={{ mx: 2.5, borderColor: alpha(isDark ? '#fff' : B.dark, .055) }} />}
                        </Box>
                    ))}
                </Box>

                <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .75 }}>
                    <LockOutlined sx={{ fontSize: 11, color: alpha(isDark ? '#fff' : B.ink, .18) }} />
                    <Typography sx={{ fontSize: '.62rem', color: alpha(isDark ? '#fff' : B.ink, .18), letterSpacing: '.05em' }}>256-BIT ENCRYPTION · NG CREDIT FINANCIAL SERVICES</Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default ReferAndEarnPage;